import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../api";
import { toast } from "sonner";
import { Loader2, QrCode, ShieldCheck } from "lucide-react";

const ACCOUNT_OPTIONS = [
  { key: "cadet", label: "Cadet", role: "cadet", is_uniformed: false },
  { key: "cfav_uniformed", label: "Uniformed CFAV", role: "cfav", is_uniformed: true },
  { key: "cfav_non_uniformed", label: "Non-uniformed CFAV", role: "cfav", is_uniformed: false },
];

export default function RegisterPage() {
  const [params] = useSearchParams();
  const requestedType = params.get("type");
  const requestedRole = params.get("role");
  const initialType =
    requestedType === "cfav_uniformed" ? "cfav_uniformed"
      : requestedType === "cfav_non_uniformed" ? "cfav_non_uniformed"
        : requestedRole === "cfav" ? "cfav_uniformed"
          : "cadet";
  const [accountType, setAccountType] = useState(initialType);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", is_uniformed: false });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const selected = ACCOUNT_OPTIONS.find((o) => o.key === accountType) || ACCOUNT_OPTIONS[0];
  const role = selected.role;
  const presetUniformed = selected.role === "cfav" ? selected.is_uniformed : false;

  const canSubmit = useMemo(() => {
    if (!form.first_name.trim()) return false;
    if (role === "cfav" && !form.email.trim()) return false;
    return true;
  }, [form, role]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = {
        role,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        is_uniformed: role === "cfav" ? presetUniformed : false,
      };
      const { data } = await api.post("/public/register-self", payload);
      setResult(data);
      toast.success("Request sent for admin approval.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account.");
    } finally {
      setBusy(false);
    }
  };

  const qrBase = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <main className="min-h-screen bg-gradient-to-br from-raf-navy via-[#0F2E5A] to-raf-blue text-white">
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="border border-white/20 bg-white/10 backdrop-blur-sm p-6 sm:p-8">
          <div className="flex items-center gap-2 text-raf-sky text-sm uppercase tracking-wide font-semibold">
            <QrCode size={15} /> QR Registration
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mt-2">Create your squadron account</h1>
          <p className="text-white/85 mt-3 text-sm sm:text-base">
            Select your account type and submit your details. Squadron admin will review and approve your request before your account is created.
          </p>

          <div className="mt-5 inline-flex border border-white/30">
            {ACCOUNT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setAccountType(opt.key)}
                className={`px-4 py-2.5 text-sm font-semibold transition-colors ${accountType === opt.key ? "bg-white text-raf-navy" : "bg-transparent text-white hover:bg-white/10"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                placeholder="First name"
                className="w-full px-3 py-2.5 text-raf-navy bg-white border border-white/30 outline-none"
              />
              <input
                value={form.last_name}
                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                placeholder="Last name"
                className="w-full px-3 py-2.5 text-raf-navy bg-white border border-white/30 outline-none"
              />
            </div>

            {role === "cfav" && (
              <>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="RAFAC email (name@rafac.mod.gov.uk)"
                  className="w-full px-3 py-2.5 text-raf-navy bg-white border border-white/30 outline-none"
                />
                <p className="text-xs text-white/80">
                  Account type selected: <strong>{presetUniformed ? "Uniformed CFAV" : "Non-uniformed CFAV"}</strong>
                </p>
              </>
            )}

            {role === "cadet" && (
              <p className="text-xs text-white/80">
                Cadet email is optional. You can still sign in with your generated username.
              </p>
            )}

            <button
              type="submit"
              disabled={busy || !canSubmit}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-raf-red text-white font-semibold hover:bg-[#A00926] transition-colors disabled:opacity-60"
            >
              {busy && <Loader2 size={16} className="animate-spin" />} Send approval request
            </button>
          </form>

          {result && (
            <div className="mt-6 bg-white text-raf-navy p-4">
              <div className="flex items-center gap-2 font-semibold"><ShieldCheck size={16} /> Request received</div>
              <div className="mt-2 text-sm">
                Status: <strong className="uppercase">{result.status || "pending"}</strong>
              </div>
              <div className="text-sm">
                Request ID: <strong>{result.request_id}</strong>
              </div>
              <p className="text-xs mt-2 text-raf-slate">{result.message || "An admin will approve or decline your request shortly."}</p>
            </div>
          )}

          <div className="mt-7 border-t border-white/20 pt-4 text-xs text-white/80 space-y-1">
            <div>Main signup QR (cadet + uniformed/non-uniformed CFAV): {qrBase}/register</div>
            <div>Optional direct QR for uniformed CFAV: {qrBase}/register?type=cfav_uniformed</div>
            <div>Optional direct QR for non-uniformed CFAV: {qrBase}/register?type=cfav_non_uniformed</div>
          </div>
        </div>
      </section>
    </main>
  );
}
